import BoilerplateForm from './boilerplate_form';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export class NewCustomerInfoForm extends BoilerplateForm {
  static get properties() {
    return {
      value: { type: String },
      token: { type: String },
      customer: { type: Object },
      region: { type: String },
      locales: { type: Object },
      hasNumber: { type: Boolean, state: true },
      hasBirthDate: { type: Boolean, state: true },
      success: { type: Boolean, state: true },
      actionSuccessUrl: { type: String, attribute: 'action-success-url' },
    };
  }

  async handleSubmit(event) {
    event.preventDefault();

    this.success = false;

    const fields = this.objectData;

    const transformedData = {
      firstName: fields.firstName,
      lastName: fields.lastName,
      ...(fields.phone ? { phone: fields.phone } : {}),
      ...(fields.birthday && !this.hasBirthDate ? { birthDate: fields.birthday } : {}),
      emailConsent: fields.emailConsent ? true : false,
      smsConsent: fields.smsConsent ? true : false,
      ...(fields.country ? { countryCode: fields.country } : {}),
      ...(fields.province ? { provinceCode: fields.province } : {}),
    }

    const body = {
      config: this.region,
      ...transformedData,
    };

    try {
      const response = await fetch(this.action, {
        method: this.method,
        body: JSON.stringify(body),
        headers: {
          'Content-Type': 'application/json',
          'x-wf-token': this.token,
          'x-wf-value': this.value,
        },
      });

      const data = await response.json();

      if (!data.success) {
        this.handleErrors(
          data.errors || data.error || 'An unknown error occurred'
        );
        return;
      }

      this.handleSuccess();
    } catch (error) {
      this.handleErrors(error);
    }
  }

  handlePhoneInput(event) {
    if (!event.target) return

    const stripped = event.target?.value?.replace(/[^0-9]/g, '').trim();
    this.hasNumber = stripped?.length > 0;
  }

  connectedCallback() {
    super.connectedCallback();
    this.hasNumber = this.customer?.phone?.replace(/[^0-9]/g, '').trim()?.length > 0;
    this.hasBirthDate = this.customer?.birth_date?.length > 0;
  }

  renderFormElements() {
    return html`
      <div class="form_split_elements">
        <float-field
          type="text"
          name="firstName"
          id="new_customer_info_form_firstName"
          label="First Name"
          required
          value=${this.customer?.first_name || ''}
          error="Please enter your first name"
          autocomplete="given-name"
          placeholder="First Name"
        ></float-field>
        <float-field
          type="text"
          name="lastName"
          id="new_customer_info_form_lastName"
          label="Last Name"
          required
          value=${this.customer?.last_name || ''}
          error="Please enter your last name"
          autocomplete="family-name"
          placeholder="Last Name"
        ></float-field>
        </div>
        <div class="form_close_elements">
        <float-date-field
          name="birthday"
          id="new_customer_info_form_birthday"
          label="Birthday"
          autocomplete="off"
          min="1900-01-01"
          ?disabled=${this.hasBirthDate}
          max=${new Date().toISOString().split('T')[0]}
          error="Please enter your birthday"
          value=${this.customer?.birth_date || ''}
          ?readonly=${this.hasBirthDate}
        ></float-date-field>
        ${
          this.locales?.edit_birth_date && this.hasBirthDate
            ? html`<div class="edit_birth_date">${unsafeHTML(this.locales?.edit_birth_date)}</div>`
            : nothing
        }
        </div>

      <float-phone-field
          name="phone"
          id="new_customer_info_form_phone"
          label="Phone"
          error="Please enter your phone number"
          autocomplete="tel"
          placeholder="Phone"
          value=${this.customer?.phone || ''}
          .countryCode=${this.customer?.country_code || ''}
          @input=${this.handlePhoneInput}
        ></float-phone-field>

      <float-country-province
        .customer=${this.customer}
      ></float-country-province>

      <float-checkbox
        type="checkbox"
        name="emailConsent"
        id="new_customer_info_form_emailConsent" 
        additional-info="${this.locales?.email_consent}"
        ?checked=${
          this.customer?.accepts_marketing ||
          this.customer?.can_precheck_consent
        }
      ></float-checkbox>

      <float-checkbox
        type="checkbox"
        name="smsConsent"
        class=${!this.hasNumber ? 'opacity-50' : ''}
        ?disabled=${!this.hasNumber}
        id="new_customer_info_form_smsConsent" 
        additional-info="${this.locales?.sms_consent}"
        ?checked=${
          this.customer?.accepts_sms === 'SUBSCRIBED' ||
          (this.customer?.can_precheck_consent && this.hasNumber)
        }
      ></float-checkbox>

      ${
        this.locales?.disclaimer
          ? html`<div class="disclaimer">${unsafeHTML(this.locales?.disclaimer)}</div>`
          : nothing
      }
      </div>
    `;
  }

  handleSuccess() {
    this.errors = {};
    this.success = true;
    this.loading = false;

    setTimeout(() => {
      window.location.href = this.actionSuccessUrl;
    }, 3000);
  }

  renderButton() {
    const successOrSubmit = this.success ? html`${this.locales?.button_success}<svg
    width="22"
    height="21"
    viewBox="0 0 22 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21 10.5C21 15.7034 16.5672 20 11 20C5.4328 20 1 15.7034 1 10.5C1 5.29663 5.4328 1 11 1C16.5672 1 21 5.29663 21 10.5Z"
      fill="white"
      stroke="white"
      stroke-width="2"
    />
    <path
      d="M6.38672 11.25L9.22543 14.25L16.3222 6.75"
      stroke="black"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg> ` : this.locales?.button_submit;
    return html`
      <button
        type="submit"
        class="btn btn-primary new_customer_info_form_button"
        aria-label="Submit new customer info form"
        ?disabled=${this.loading || this.success}
        aria-busy=${this.loading ? 'true' : 'false'}
      >
        ${this.loading
          ? html`<bouncing-ellipsis></bouncing-ellipsis>`
          : successOrSubmit}
      </button>
    `;
  }
}

window.customElements.get('new-customer-info-form') ||
  window.customElements.define('new-customer-info-form', NewCustomerInfoForm);
